import { useState } from "react";
import { Card, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { EventThread } from "@/components/EventThread";
import { Video, MessageSquare, FileDown, CalendarClock } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "date to be confirmed");

// School User dashboard: every event they asked for, with the join link, slides and message thread.
export const MyEvents = ({ events, onChanged }) => {
  const [thread, setThread] = useState(null);
  const rows = events || [];
  return (
    <Card title="Your events" data-testid="my-events-card">
      <p className="text-sm text-zinc-500 mb-3">Events you've requested. Join links, slides and questions all live here — no need to dig through email.</p>
      {rows.length === 0 ? <EmptyState text="No events yet. Use “Request an Event” above to ask your coordinator to host one." /> : (
        <div className="space-y-2">
          {rows.map((ev) => (
            <div key={ev.id} className="border rounded-md px-3 py-2.5" data-testid={`my-event-${ev.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-zinc-800">{ev.title}</span>
                <StatusBadge status={ev.status} />
                <span className="text-xs text-zinc-400 flex items-center gap-1"><CalendarClock className="w-3 h-3" />{fmt(ev.scheduled_at)}</span>
                <div className="ml-auto flex flex-wrap gap-1.5">
                  {ev.daily_room_url && (
                    <a href={ev.daily_room_url} target="_blank" rel="noreferrer">
                      <Button size="sm" className="h-7 text-xs bg-cc-green hover:bg-green-600 text-white" data-testid={`my-event-join-${ev.id}`}><Video className="w-3 h-3 mr-1" />Join</Button>
                    </a>
                  )}
                  <Button size="sm" variant={ev.unread_messages ? "default" : "outline"}
                    className={`h-7 text-xs ${ev.unread_messages ? "bg-cc-orange hover:bg-orange-600 text-white cc-flash" : ""}`}
                    onClick={() => setThread(ev)} data-testid={`my-event-messages-${ev.id}`}>
                    <MessageSquare className="w-3 h-3 mr-1" />
                    {ev.unread_messages ? `${ev.unread_messages} new message${ev.unread_messages > 1 ? "s" : ""}` : `Messages${ev.message_count ? ` (${ev.message_count})` : ""}`}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-1">{ev.event_type}{ev.decline_reason ? ` · ${ev.decline_reason}` : ""}</p>
              {(ev.slides || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {ev.slides.map((s, i) => (
                    <a key={i} href={s.download_url} className="text-xs text-cc-blue inline-flex items-center gap-1 hover:underline" data-testid={`my-event-slide-${ev.id}-${i}`}>
                      <FileDown className="w-3 h-3" />{s.file_name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <EventThread event={thread} onClose={() => { setThread(null); onChanged?.(); }} onSent={onChanged} />
    </Card>
  );
};
